import type {
  TMajorActionPlayToNeighborhoodData,
  TPortfolioNeighborhoodId,
} from '@kapitalist/contract/types'
import {skins} from '~/game/skins'
import {BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID} from '~/game/types/neighborhoodId'
import type ResidenceCard from '~/models/game/Cards/ResidenceCard'
import {neighborhoodCardFactory} from '~/models/game/factories'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {TMajorActionUserFacingDescriptionData} from '~/models/game/MajorAction'
import MajorAction from '~/models/game/MajorAction'
import {mUnambiguousNeighborhoodDescriptionDataFromGamePosition} from '~/models/game/MajorActions/utils'
import type {AnyPropertyCard} from '~/models/game/types'
import {mutateIf} from '~/utils/etc'
import {inTense, sentenceCase} from '~/utils/language'

/**
 *  Used to play a card from your hand.
 *  MajorActionChangeNeighborhood moves a SpecialPropertyCard to a different neighborhood.
 */
export default class MajorActionPlayToNeighborhood extends MajorAction<TMajorActionPlayToNeighborhoodData> {
  card: ResidenceCard | AnyPropertyCard

  // NB: a new neighborhood will not have an ID yet, so this can be BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID
  // if the (property) card being placed is creating a new neighborhood.
  portfolioNeighborhoodId: TPortfolioNeighborhoodId

  constructor(data: TMajorActionPlayToNeighborhoodData) {
    super(data)
    this.card = neighborhoodCardFactory(data.card)
    this.portfolioNeighborhoodId = data.portfolioNeighborhoodId
  }

  public userFacingDescription({
    tense,
    skin,
    gamePosition,
  }: TMajorActionUserFacingDescriptionData): string {
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const majorActor = gamePosition.majorActorPlayer
    const dropFirstNoun = tense === 'imperativePrompt'

    const possessive =
      dropFirstNoun || currentPlayer?.id === majorActor.id ? 'your' : 'their'
    const pnData = mUnambiguousNeighborhoodDescriptionDataFromGamePosition(
      gamePosition,
      this.portfolioNeighborhoodId
    )
    const neighborhoodColor =
      this.portfolioNeighborhoodId === BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID || !pnData
        ? undefined
        : pnData.portfolioNeighborhood.displayColorShort(skin)

    return skins[skin].gameLogDescriptions.majorActions.playToNeighborhood({
      tense,
      actor: dropFirstNoun
        ? undefined
        : currentPlayer?.id === majorActor.id
          ? 'You'
          : majorActor.username,
      placeVerb: mutateIf(inTense(tense)('place'), dropFirstNoun, sentenceCase),
      cardName: this.card.displayName(skin),
      possessive,
      neighborhoodColor,
    })
  }
}
